/**
 * Transform WooCommerce Store API item to cart product format
 *
 * @since 1.0.0
 */

import type { RedFoxCartProduct } from '@/frontend/types/cart';
import { formatCurrencyPrice } from '@/frontend/utils/format-currency-price';

import type { StoreApiItem } from '../types';

/**
 * Transform WooCommerce Store API item to cart product format
 *
 * @param item - Store API cart item
 */
export const transformStoreApiItem = (item: Record<string, unknown>): RedFoxCartProduct => {
  const typedItem = item as StoreApiItem;

  // Handle WooCommerce Store API price structure
  const pricesData = typedItem.prices;

  // Get raw price values (they're strings in the API response)
  const rawRegularPrice = pricesData?.regular_price ?? pricesData?.price ?? '0';
  const rawSalePrice = pricesData?.sale_price ?? pricesData?.price ?? '0';

  // Convert to numbers and apply currency minor unit (divide by 10^minor_unit)
  const minorUnit = pricesData?.currency_minor_unit ?? 2;
  const divisor = Math.pow(10, minorUnit);

  const regularPrice = Number(rawRegularPrice) / divisor;
  const salePrice = Number(rawSalePrice) / divisor;

  // Format price with proper currency
  const currencySymbol = pricesData?.currency_symbol ?? '$';
  const formattedPrice = formatCurrencyPrice(salePrice, { currencySymbol });

  // Handle image URL from images array
  const images = typedItem.images;
  const imageUrl = images?.[0]?.src ?? '';
  const imageAlt = images?.[0]?.alt ?? String(typedItem.name ?? '');

  return {
    name: String(typedItem.name ?? 'Unknown Product'),
    regular_price: regularPrice,
    sale_price: salePrice,
    formatted_price: formattedPrice,
    quantity: Number(typedItem.quantity ?? 1),
    image_url: imageUrl,
    image_alt: imageAlt,
    is_on_sale:
      Boolean(typedItem.is_on_sale) ||
      (regularPrice > 0 && salePrice > 0 && salePrice < regularPrice),
    is_variation: Boolean(typedItem.is_variation) || typedItem.type === 'variation',
    variation_data:
      (typedItem.variation_data as RedFoxCartProduct['variation_data']) ??
      (typedItem.variation
        ? {
            variation_id: Number(typedItem.id),
            attributes:
              typedItem.variation?.map((attr) => ({
                name: attr.attribute,
                label: attr.attribute,
                value: attr.value,
                formatted_value: attr.value,
              })) ?? [],
            formatted_name:
              typedItem.variation?.map((attr) => `${attr.attribute}: ${attr.value}`).join(', ') ??
              '',
          }
        : undefined),
  };
};
